import React from 'react';
import { useSEO } from '../hooks/useSEO';
import { useBanner } from '../contexts/BannerContext';

export const PrivacyPage: React.FC = () => {
  useSEO('privacy' as any);
  const { isBannerVisible, bannerHeight } = useBanner();

  return (
    <main 
      className="pb-20"
      style={{ 
        paddingTop: isBannerVisible ? `${bannerHeight + 100}px` : '100px'
      }}
    >
      <section className="px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <header className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-blue-600 to-blue-700 bg-clip-text text-transparent">
              Privacy Policy
            </h1>
            <p className="text-gray-700 dark:text-gray-300 text-lg">
              Last updated: {new Date().toLocaleDateString()}
            </p>
          </header>

          <div className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-3xl border border-gray-200 dark:border-white/10 p-8">
            <div className="prose prose-invert max-w-none">
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">1. Information We Collect</h2>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  At Cognitive Craft Hub, we collect information that you provide directly to us, such as when you:
                </p>
                <ul className="text-gray-700 dark:text-gray-300 list-disc ml-6 mb-4 space-y-2">
                  <li>Fill out our contact form or request a consultation</li>
                  <li>Subscribe to our newsletter or communications</li>
                  <li>Use our services or interact with our website</li>
                  <li>Contact us via email, phone, or other communication channels</li>
                </ul>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  This information may include your name, email address, phone number, company name, 
                  and any other information you choose to provide.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">2. How We Use Your Information</h2>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  We use the information we collect to:
                </p>
                <ul className="text-gray-700 dark:text-gray-300 list-disc ml-6 mb-4 space-y-2">
                  <li>Provide, maintain, and improve our services</li>
                  <li>Respond to your inquiries and provide customer support</li>
                  <li>Send you technical notices, security alerts, and administrative messages</li>
                  <li>Communicate with you about our services, offers, and events</li>
                  <li>Process transactions and send related information</li>
                  <li>Monitor and analyze usage patterns and trends</li>
                </ul>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  This includes all our services such as AI solutions, digital marketing automation, cloud infrastructure,
                  custom software development, robotics implementation consulting, blockchain technology, and other specialized services.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">3. Information Sharing</h2>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  We do not sell, trade, or otherwise transfer your personal information to third parties 
                  without your consent, except in the following circumstances:
                </p>
                <ul className="text-gray-700 dark:text-gray-300 list-disc ml-6 mb-4 space-y-2">
                  <li>When required by law or to protect our rights and safety</li>
                  <li>With trusted service providers who assist us in operating our business</li>
                  <li>In connection with a merger, acquisition, or sale of assets</li>
                  <li>With your explicit consent</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">4. Data Security</h2>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  We implement appropriate technical and organizational measures to protect your personal 
                  information against unauthorized access, alteration, disclosure, or destruction. These measures include:
                </p>
                <ul className="text-gray-700 dark:text-gray-300 list-disc ml-6 mb-4 space-y-2">
                  <li>Encryption of data in transit and at rest</li>
                  <li>Regular security assessments and updates</li>
                  <li>Limited access to personal information on a need-to-know basis</li>
                  <li>Secure data centers and infrastructure</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">5. Cookies and Tracking</h2>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  Our website may use cookies and similar tracking technologies to enhance your experience. 
                  These technologies help us:
                </p>
                <ul className="text-gray-700 dark:text-gray-300 list-disc ml-6 mb-4 space-y-2">
                  <li>Remember your preferences and settings</li>
                  <li>Analyze website traffic and usage patterns</li>
                  <li>Provide personalized content and advertisements</li>
                  <li>Improve our website functionality and performance</li>
                </ul>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  You can control cookie settings through your browser preferences.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">6. Your Rights</h2>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  You have the right to:
                </p>
                <ul className="text-gray-700 dark:text-gray-300 list-disc ml-6 mb-4 space-y-2">
                  <li>Access the personal information we hold about you</li>
                  <li>Correct any inaccurate or incomplete information</li>
                  <li>Request deletion of your personal information</li>
                  <li>Object to or restrict the processing of your information</li>
                  <li>Withdraw consent at any time</li>
                  <li>Request a copy of your data in a portable format</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">7. Data Retention</h2>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  We retain your personal information only for as long as necessary to fulfill the purposes 
                  outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. 
                  When we no longer need your information, we securely delete or anonymize it.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">8. Third-Party Services</h2>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  Our website may contain links to third-party services or websites. This Privacy Policy does not 
                  apply to those external sites. We encourage you to review the privacy policies of any third-party 
                  services you use.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">9. Children's Privacy</h2>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  Our services are not intended for children under the age of 13. We do not knowingly collect 
                  personal information from children under 13. If we become aware that we have collected such 
                  information, we will take steps to delete it promptly.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">10. Changes to This Policy</h2>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  We may update this Privacy Policy from time to time. We will notify you of any material changes 
                  by posting the new Privacy Policy on this page and updating the "Last updated" date. Your continued 
                  use of our services after such changes constitutes your acceptance of the updated policy.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">11. Contact Us</h2>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  If you have any questions about this Privacy Policy or our data practices, please contact us:
                </p>
                <div className="bg-gray-100 dark:bg-black/20 p-4 rounded-lg">
                  <p className="text-gray-700 dark:text-gray-300">
                    <strong className="text-gray-900 dark:text-white">Cognitive Craft Hub</strong><br />
                    Offices: East Lansing, Michigan & Houston, Texas<br />
                    Email: team@cognitivecrafthub.com<br />
                    Phone: <a href="tel:+15172025360" className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">+1 (517) 202-5360</a>
                  </p>
                </div>
              </section>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};